/**
 * ServicePageHero - Reusable hero section for service-specific pages
 * Used on Flood Cleanup, Mold Removal, Smoke Damage, etc.
 */

import { useEffect } from 'react';

interface ServicePageHeroProps {
  title: string;
  subtitle: string;
  backgroundImage: string;
  contactSectionId?: string;
}

export default function ServicePageHero({
  title,
  subtitle,
  backgroundImage,
  contactSectionId = 'contact',
}: ServicePageHeroProps) {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById(contactSectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-[500px] sm:min-h-[550px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('${backgroundImage}')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28 md:py-32 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
          {title}
        </h1>
        <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed px-2">
          {subtitle}
        </p>
        
        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-2">
          <a
            href="tel:3853406632"
            className="w-full sm:w-auto text-center bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-200 hover:brightness-90 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Call (385) 340-6632
          </a>
          <button
            onClick={scrollToContact}
            className="w-full sm:w-auto bg-white text-sky-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-200 hover:brightness-90 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
          >
            Get Free Estimate
          </button>
        </div>
      </div>
    </section>
  );
}
